import { useState } from "react";
import { Button } from "@/ui/button";
import { Modal } from "@/ui/modals";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import FilterRow from "./filter-row";
import { MinMaxInput } from "@/ui/min-max-input";
import FilterToggleButton from "@/ui/filter-toggle-button";
import { useTransactionsFilterContext } from "@/providers/transactions-filter/transactions-filter";
import {
  TRANSACTION_TYPES,
  TransactionFilters,
} from "@/api/transactions/types";

const TransactionsFiltersModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { filters, setFilters } = useTransactionsFilterContext();

  const [formData, setFormData] = useState<TransactionFilters>(filters);

  const updateForm = <K extends keyof TransactionFilters>(
    key: K,
    value: TransactionFilters[K] | ""
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value !== "" ? value : undefined,
    }));
  };

  const handleDone = () => {
    setFilters(formData);
    setIsOpen(false);
  };

  const handleClear = () => {
    const cleared: TransactionFilters = {};

    setFilters(cleared);
    setFormData(cleared);
    setIsOpen(false);
  };

  return (
    <>
      <GradientBorderRadius
        gradient="default-border"
        radius="bigger"
        hover="r-100"
        className="h-full"
      >
        <button
          onClick={() => setIsOpen(true)}
          className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
        >
          Filters
        </button>
      </GradientBorderRadius>

      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        className="w-[28.125rem] py-[0.9375rem] px-[0.6250rem]"
      >
        <Modal.Title title="Filters" className="text-xs font-semibold" />
        <div className="w-full h-full grid grid-cols-[max-content,1fr] gap-y-[0.375rem] gap-x-[0.625rem] text-white text-xs font-semibold">
          <FilterRow title="Type" className="h-[1.375rem]">
            <div className="h-full grid grid-cols-2 gap-[0.3125rem]">
              {TRANSACTION_TYPES.map((t) => (
                <FilterToggleButton
                  key={t}
                  title={t}
                  selected={formData.transactionType === t}
                  className="capitalize"
                  onClick={() =>
                    updateForm(
                      "transactionType",
                      t as TransactionFilters["transactionType"]
                    )
                  }
                />
              ))}
            </div>
          </FilterRow>
          <FilterRow title="Price">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formData.priceMin}
              maxValue={formData.priceMax}
              onMinChange={(val) => updateForm("priceMin", val)}
              onMaxChange={(val) => updateForm("priceMax", val)}
            />
          </FilterRow>
          <FilterRow title="Value">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formData.valueMin}
              maxValue={formData.valueMax}
              onMinChange={(val) => updateForm("valueMin", val)}
              onMaxChange={(val) => updateForm("valueMax", val)}
            />
          </FilterRow>
          <FilterRow title="Time">
            <MinMaxInput
              unitSide="right"
              text="h"
              layout="row"
              minValue={formData.ageHoursMin}
              maxValue={formData.ageHoursMax}
              onMinChange={(val) => updateForm("ageHoursMin", val)}
              onMaxChange={(val) => updateForm("ageHoursMax", val)}
            />
          </FilterRow>
        </div>
        <Modal.Footer>
          <Button
            title="Done"
            active={false}
            className="w-[9.4375rem] font-semibold text-xs"
            onClick={handleDone}
          />
          <Button
            title="Clear"
            active={false}
            className="w-[9.4375rem] font-semibold text-xs"
            onClick={handleClear}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TransactionsFiltersModal;
